/**
 * Dashnode AI Backend API Service
 * Integrates directly with Frappe whitelisted endpoints in dashnode_ai.api
 */

const BASE_URL = '/api/method/dashnode_ai.api';

/**
 * Infer group name based on key name or content
 */
export function inferGroup(key = '', content = '') {
  const k = `${key || ''} ${content || ''}`.toLowerCase();
  if (['firm', 'founded', 'model', 'engineering_team', 'company', 'organization'].some((w) => k.includes(w))) {
    return 'Company';
  }
  if (['program', 'product', 'assembly', 'barrel', 'core wall', 'support_ring', 'crd'].some((w) => k.includes(w))) {
    return 'Programs & Products';
  }
  if (['tool', 'software', 'cad', 'solidworks', 'catia', 'ansys', 'plm', 'erp', 'fluent'].some((w) => k.includes(w))) {
    return 'Tools in Use';
  }
  if (['material', 'inconel', 'stainless', 'steel', '316l', '17_4', 'sa_508', 'borated'].some((w) => k.includes(w))) {
    return 'Qualified Materials';
  }
  if (['code', 'standard', 'asme', 'rcc', 'iso', 'tolerance', 'fatigue', 'seal_face'].some((w) => k.includes(w))) {
    return 'Codes & Standards';
  }
  if (['supplier', 'vendor', 'lead_time', 'lt_heavy', 'walchandnagar', 'godrej', 'mtar', 'bhel'].some((w) => k.includes(w))) {
    return 'Qualified Fab Suppliers';
  }
  if (['decision', 'past', 'rationale', 'history', 'shield_wall', 'gen_2', 'switched', 'chose', 'rejected'].some((w) => k.includes(w))) {
    return 'Past Decisions';
  }
  return null;
}

/**
 * Normalize group name for uniform grouping
 */
export function normalizeGroup(group = '') {
  if (!group || typeof group !== 'string') return 'General';
  const trimmed = group.trim();
  if (!trimmed) return 'General';
  if (trimmed === trimmed.toLowerCase()) {
    return trimmed.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return trimmed;
}

/**
 * Format raw key name into human readable title
 */
export function formatKey(key = '') {
  if (!key) return '';
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Fetch all conversation threads for current user
 */
export async function fetchThreads(userId = 'guest-user') {
  try {
    const url = `${BASE_URL}.list_threads?user_id=${encodeURIComponent(userId)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: Failed to fetch threads`);
    }

    const data = await response.json();
    const threads = data?.message?.threads || [];
    return { success: true, threads };
  } catch (err) {
    console.warn('Backend list_threads failed or offline, using cached/fallback:', err);
    return { success: false, threads: [], error: err.message };
  }
}

/**
 * Fetch messages for a specific conversation thread
 */
export async function fetchThreadMessages(threadId, userId = 'guest-user') {
  try {
    const url = `${BASE_URL}.get_thread_messages?thread_id=${encodeURIComponent(
      threadId
    )}&user_id=${encodeURIComponent(userId)}&include_tools=1`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: Failed to fetch messages`);
    }

    const data = await response.json();
    return { success: true, messages: data?.message?.messages || [] };
  } catch (err) {
    console.warn(`Backend get_thread_messages(${threadId}) failed:`, err);
    return { success: false, messages: [], error: err.message };
  }
}

/**
 * Delete a conversation thread
 */
export async function deleteThread(threadId, userId = 'guest-user') {
  try {
    const response = await fetch(`${BASE_URL}.delete_thread`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        thread_id: threadId,
        user_id: userId,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return { success: true, data: data?.message };
  } catch (err) {
    console.warn(`Backend delete_thread(${threadId}) failed:`, err);
    return { success: false, error: err.message };
  }
}

/**
 * Upload CAD STEP file (and optional 2D technical drawing) to create a Part workspace
 * Endpoint: POST /api/method/dashnode_ai.api.create_part
 */
export async function createPart({
  file,
  drawingFile = null,
  name = null,
  userId = 'guest-user',
  analyze = true,
  modelType = 'auto',
}) {
  try {
    const formData = new FormData();
    if (file) {
      formData.append('step_file', file, file.name);
    }
    if (drawingFile) {
      formData.append('drawing_file', drawingFile, drawingFile.name);
    }
    if (name) {
      formData.append('name', name);
    } else if (file) {
      formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
    }
    if (userId) {
      formData.append('user_id', userId);
    }
    formData.append('analyze', analyze ? '1' : '0');
    formData.append('model_type', modelType);

    const response = await fetch(`${BASE_URL}.create_part`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      let errorMsg = `HTTP ${response.status}: Failed to upload CAD model`;
      const isAuth = response.status === 401;
      try {
        const errorData = await response.json();
        if (errorData._server_messages) {
          const parsed = JSON.parse(errorData._server_messages);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const inner = JSON.parse(parsed[0]);
            if (inner.message) errorMsg = inner.message;
          }
        } else if (errorData.exception) {
          errorMsg = errorData.exception.split(':').pop().trim();
        }
      } catch {
        // fallback to default error message
      }
      return { success: false, error: errorMsg, requiresAuth: isAuth };
    }

    const data = await response.json();
    return { success: true, part: data?.message };
  } catch (err) {
    console.warn('Backend create_part failed:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Send chat message to autonomous multi-agent backend (synchronous)
 */
export async function sendChatMessage({
  message,
  threadId,
  partId = null,
  agent = null,
  userId = 'guest-user',
}) {
  try {
    const response = await fetch(`${BASE_URL}.chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        message,
        thread_id: threadId,
        part_id: partId,
        agent,
        user_id: userId,
      }),
    });

    if (!response.ok) {
      const isAuthError = response.status === 401;
      let errorMsg = isAuthError ? 'Authentication required. Please sign in.' : `HTTP ${response.status}`;
      try {
        const errData = await response.json();
        if (errData.message?.error) {
          errorMsg = errData.message.error;
        } else if (errData.exception) {
          errorMsg = errData.exception;
        } else if (errData._server_messages) {
          const parsed = JSON.parse(errData._server_messages);
          const msgs = parsed.map((m) => {
            try {
              return JSON.parse(m).message;
            } catch {
              return m;
            }
          });
          errorMsg = msgs.join('\n');
        }
      } catch {}
      return { success: false, requiresAuth: isAuthError, error: errorMsg };
    }

    const data = await response.json();
    const result = data?.message;

    if (result?.error) {
      const isAuth = result.requires_auth || result.error.toLowerCase().includes('not logged in') || result.error.toLowerCase().includes('unauthorized');
      return { success: false, requiresAuth: isAuth, error: result.error };
    }

    return { success: true, data: result };
  } catch (err) {
    console.warn('Backend chat execution error:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Stream real-time chat execution events over Server-Sent Events (SSE)
 * Directly connects to /api/method/dashnode_ai.api.chat_stream
 */
export async function streamChatMessage({
  message,
  threadId,
  partId = null,
  agent = null,
  userId = 'guest-user',
  onEvent,
  signal,
}) {
  const url = `${BASE_URL}.chat_stream`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    },
    credentials: 'include',
    body: JSON.stringify({
      message,
      thread_id: threadId,
      part_id: partId,
      agent,
      user_id: userId,
    }),
    signal,
  });

  if (!response.ok) {
    const isAuth = response.status === 401;
    let errorMsg = isAuth ? 'Authentication required. Please sign in.' : `HTTP ${response.status}`;
    try {
      const errData = await response.json();
      if (errData.message?.error) {
        errorMsg = errData.message.error;
      } else if (errData.exception) {
        errorMsg = errData.exception;
      }
    } catch {}
    const err = new Error(errorMsg);
    err.status = response.status;
    err.requiresAuth = isAuth;
    throw err;
  }

  if (!response.body) {
    throw new Error('ReadableStream not supported by browser environment.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed || trimmed.startsWith(':')) continue; // heartbeat or empty line

      const eventMatch = trimmed.match(/^event:\s*(.+)$/m);
      const dataMatch = trimmed.match(/^data:\s*(.+)$/m);

      if (dataMatch) {
        const eventType = eventMatch ? eventMatch[1].trim() : 'message';
        try {
          const payload = JSON.parse(dataMatch[1]);
          if (onEvent) onEvent(eventType, payload);
        } catch {
          if (onEvent) onEvent(eventType, { raw: dataMatch[1] });
        }
      }
    }
  }
}

/**
 * Fetch memory knowledge from Frappe Memory DocType
 * Supports keys: group, subtitle, memory_content as well as memory_key, memory_context, content, key
 */
export async function fetchMemory(userId = 'guest-user', partId = null) {
  try {
    let url = `${BASE_URL}.get_memory?user_id=${encodeURIComponent(userId)}`;
    if (partId) url += `&part_id=${encodeURIComponent(partId)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    const memData = data?.message || {};

    let rawList = [];
    if (Array.isArray(memData)) {
      rawList = memData;
    } else if (Array.isArray(memData.memories)) {
      rawList = memData.memories;
    } else if (Array.isArray(memData.items)) {
      rawList = memData.items;
    } else if (typeof memData === 'object') {
      rawList = [
        ...(memData.shop || []),
        ...(memData.user || []),
        ...(memData.part || []),
        ...(memData.general || []),
      ];
    }

    const combined = rawList.map((m) => {
      const rawKey = m.subtitle || m.Subtitle || m.memory_key || m.key || m.id || '';
      const rawSubtitle = m.subtitle || m.Subtitle || m.memory_key || formatKey(rawKey) || 'Note';
      const rawContent = m.memory_content ?? m.MemoryContent ?? m['Memory Content'] ?? m.memory_context ?? m.content ?? m.memory ?? m.text ?? '';
      const rawGroup = normalizeGroup(
        m.group ||
        m.Group ||
        inferGroup(rawKey, rawContent) ||
        (m.scope ? m.scope.charAt(0).toUpperCase() + m.scope.slice(1) : 'General')
      );

      return {
        id: m.id || m.key || rawSubtitle || `mem-${Math.random().toString(36).substring(2, 8)}`,
        group: rawGroup,
        subtitle: rawSubtitle,
        memory_content: rawContent,
        // Aliases for compatibility
        memory_key: rawSubtitle,
        memory_context: rawContent,
        text: rawContent,
        key: m.key || rawSubtitle,
        scope: m.scope || (rawGroup ? rawGroup.toLowerCase() : 'shop'),
        kind: m.kind || 'Preference',
        at: m.at || m.creation || m.modified || null,
        raw: m,
      };
    });

    return { success: true, memoryList: combined };
  } catch (err) {
    console.warn('Backend get_memory failed:', err);
    return { success: false, memoryList: [] };
  }
}

/**
 * Add a memory constraint or preference
 * Accepts { group, subtitle, memory_content, memory_key, memory_context, content, scope, kind, userId, partId }
 */
export async function addMemory({
  group = 'Company',
  subtitle = null,
  memory_content = null,
  content = null,
  memory_key = null,
  memory_context = null,
  scope = 'shop',
  kind = 'preference',
  userId = 'guest-user',
  partId = null,
}) {
  try {
    const finalGroup = normalizeGroup(group || 'General');
    const finalSubtitle = subtitle || memory_key || (kind ? `${kind}-${Date.now().toString(36)}` : 'Note');
    const finalContent = memory_content || memory_context || content || '';

    const response = await fetch(`${BASE_URL}.add_memory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        content: finalContent,
        scope: (scope || 'shop').toLowerCase(),
        kind,
        key: finalSubtitle,
        group: finalGroup,
        subtitle: finalSubtitle,
        memory_content: finalContent,
        memory_key: finalSubtitle,
        memory_context: finalContent,
        user_id: userId,
        part_id: partId,
      }),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return { success: true, data: data?.message };
  } catch (err) {
    console.warn('Backend add_memory failed:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Delete a memory constraint
 */
export async function deleteMemory(key, scope = 'shop', userId = 'guest-user') {
  try {
    const response = await fetch(`${BASE_URL}.delete_memory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ key, scope: (scope || 'shop').toLowerCase(), user_id: userId }),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return { success: true, data: data?.message };
  } catch (err) {
    console.warn(`Backend delete_memory(${key}) failed:`, err);
    return { success: false, error: err.message };
  }
}

/**
 * Resolve Human Approval Gate action
 */
export async function resolveApproval(actionId, decision, threadId = null) {
  try {
    const response = await fetch(`${BASE_URL}.resolve_approval`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        action_id: actionId,
        decision,
        thread_id: threadId,
      }),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return { success: true, data: data?.message };
  } catch (err) {
    console.warn(`Backend resolve_approval(${actionId}) failed:`, err);
    return { success: false, error: err.message };
  }
}
